import React, { useState } from 'react';
import { authHeaders } from '../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const useManualMatch = ({ slug, user, members, onMatchCreated, t, setConfirmModal }) => {
  const [isActive, setIsActive] = useState(false);
  const [selected, setSelected] = useState([]);
  const [isCreating, setIsCreating] = useState(false);

  const toggleSelection = (username) => {
    if (isCreating) return;
    setSelected(prev => {
      if (prev.includes(username)) {
        return prev.filter(u => u !== username);
      }
      if (prev.length >= 2) return [...prev];
      return [...prev, username];
    });
  };

  const isSelectable = (member) => {
    if (!isActive || isCreating) return false;
    if (member.matched) return false;
    return true;
  };

  const isMemberSelected = (username) => selected.includes(username);

  const handleActivate = () => {
    setIsActive(true);
    setSelected([]);
  };

  const handleCancel = () => {
    setIsActive(false);
    setSelected([]);
  };

  const handleCreatePair = () => {
    if (selected.length !== 2) return;
    setConfirmModal({
      message: t('community.manual_match_confirm', { user1: selected[0], user2: selected[1] }),
      onConfirm: () => submitManualMatch()
    });
  };

  const submitManualMatch = async () => {
    setConfirmModal(null);
    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/community/${slug}/manual-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          username1: selected[0],
          username2: selected[1]
        })
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || t('community.manual_match_failed'));
        return;
      }
      if (data.notificationStatus === 'Sent') {
        alert(t('community.manual_match_success'));
      } else if (data.notificationStatus === 'Partial') {
        alert(t('community.manual_match_partial'));
      } else {
        alert(t('community.manual_match_no_notify'));
      }
      onMatchCreated(selected[0], selected[1]);
    } catch (err) {
      alert(t('community.manual_match_failed'));
    } finally {
      setIsCreating(false);
      setIsActive(false);
      setSelected([]);
    }
  };

  return {
    isActive,
    selected,
    isCreating,
    isSelectable,
    isMemberSelected,
    toggleSelection,

    renderToggleButton: () => (
      <button
        onClick={isActive ? handleCancel : handleActivate}
        disabled={isCreating}
        style={{
          padding: '6px 14px',
          borderRadius: '6px',
          border: isActive ? '1px solid #dc2626' : '1px solid #6366f1',
          backgroundColor: isActive ? '#fef2f2' : '#f5f3ff',
          color: isActive ? '#dc2626' : '#6366f1',
          fontSize: '13px',
          fontWeight: '600',
          cursor: isCreating ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
      >
        {isActive ? t('community.manual_match_cancel') : t('community.manual_match_btn')}
      </button>
    ),

    renderSelectionBar: () => {
      if (!isActive) return null;

      const selectedNames = selected.map(username => {
        const m = (members || []).find(member => member.username === username);
        return m ? (m.name || `@${username}`) : `@${username}`;
      });

      return (
        <div style={{
          padding: '12px 16px',
          backgroundColor: selected.length === 2 ? '#f0fdf4' : '#f5f3ff',
          borderRadius: '8px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          border: selected.length === 2 ? '1px solid #bbf7d0' : '1px solid #e0e7ff',
          transition: 'all 0.2s'
        }}>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {selected.length === 0 && (
              <span>{t('community.manual_match_hint')}</span>
            )}
            {selected.length > 0 && (
              <span>
                {selectedNames.join(' + ')}
                {selected.length < 2 && (
                  <span style={{ color: '#9ca3af', marginLeft: '6px' }}>
                    ({t('community.manual_match_count', { count: selected.length })})
                  </span>
                )}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {selected.length > 0 && (
              <button
                onClick={() => setSelected([])}
                disabled={isCreating}
                style={{
                  padding: '5px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#6b7280',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                {t('community.manual_match_clear')}
              </button>
            )}
            {selected.length === 2 && (
              <button
                onClick={handleCreatePair}
                disabled={isCreating}
                style={{
                  padding: '5px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  opacity: isCreating ? 0.6 : 1
                }}
              >
                {isCreating ? t('community.manual_match_creating') : t('community.manual_match_create')}
              </button>
            )}
          </div>
        </div>
      );
    }
  };
};

export default useManualMatch;
